import { useState } from 'react';
import { toast } from 'sonner';

import { DuplicateQueryModal } from '@/sections/insights/queryBuilder/components/Modals';
import { QueryResults } from '@/sections/insights/queryBuilder/components/QueryResults';
import { useQueryOperations } from '@/sections/insights/queryBuilder/hooks/useQueryOperations';
import { useQueryBuilder } from '@/sections/insights/queryBuilder/state/useQueryBuilder';
import { useAuth } from '@/state/auth';

import { ComingSoonPlaceholder } from './ComingSoonPlaceholder';
import { EmptyState } from './EmptyState';
import { QuerySplitLayout } from './QuerySplitLayout';
import { QuerySimpleVisualizer } from './QuerySplitVisualizer';
import { ActionsToolbar } from './QuerySplitVisualizer/ActionsToolbar';

/**
 * QuerySplitViewer - Read-only query interface with split-pane layout
 *
 * Layout:
 * - Left pane: QuerySimpleVisualizer (Praetorian users only) or Coming Soon placeholder
 * - Right pane: EmptyState initially, QueryResults after execution
 * - No action panel (as per design requirements)
 *
 * Behavior:
 * - Same as QuerySplitEditor but read-only
 * - Matches QueryWriter pattern: EmptyState → QueryResults
 * - Storage key: "querySplitViewerRatio"
 * - Feature gated to Praetorian users during beta
 *
 * Triggered by:
 * - QueryMenu "View Query (Split View)" action
 * - QueryBuilderWorkstation when mode === 'split-view'
 */
export const QuerySplitViewer = () => {
  const { queryState, isGlobal, getSavedQuery } = useQueryBuilder();
  const { isPraetorianUser } = useAuth();
  const [showResults, setShowResults] = useState(false);

  // Add action handlers (NOTE: No handleSave for viewer)
  const { isSaving } = useQueryOperations();
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);

  const handleDuplicate = () => {
    setIsDuplicateModalOpen(true);
  };

  const handleExecute = () => {
    setShowResults(true);
  };

  // Error handling for missing query
  if (!queryState) {
    toast.error('No query selected');
    return null;
  }

  return (
    <>
      <QuerySplitLayout
        leftContent={
          <>
            <ActionsToolbar
              editable={false}
              isGlobal={isGlobal}
              onExecute={handleExecute}
              onDuplicate={handleDuplicate}
              isSaving={isSaving}
              // NO onSave prop - viewer is read-only
            />

            <div className="relative h-full">
              {isPraetorianUser ? (
                <QuerySimpleVisualizer editable={false} />
              ) : (
                <ComingSoonPlaceholder />
              )}
            </div>
          </>
        }
        rightContent={
          showResults ? (
            <QueryResults selectedQuery={getSavedQuery()} />
          ) : (
            <EmptyState />
          )
        }
        storageKey="querySplitViewerRatio"
        testId="query-split-viewer"
      />

      {/* Duplicate Modal */}
      {getSavedQuery() && (
        <DuplicateQueryModal
          isOpen={isDuplicateModalOpen}
          onClose={() => setIsDuplicateModalOpen(false)}
          originalQuery={getSavedQuery()!}
        />
      )}
    </>
  );
};
